import type { TranslationMap } from '../i18n';

export const en: TranslationMap = {
  // Auth - Common
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.firstName': 'First Name',
  'auth.lastName': 'Last Name',
  'auth.back': '← Back',
  'auth.acceptTerms': 'I accept the',
  'auth.termsAndConditions': 'Terms & Conditions',

  'auth.verificationRequired': 'Verification Required',
  'auth.verificationLinkSent': 'Please click the verification link in your inbox.',
  'auth.sending': 'Sending...',
  'auth.resendLink': 'Resend Link',
  'auth.phonePlaceholder': '(xxx) xxx-xxxx',

  // Auth - Login
  'auth.signIn': 'Sign In',
  'auth.signingIn': 'Signing In...',
  'auth.resetPassword': 'Reset Password',
  'auth.createAccount': 'Create Account',
  'auth.logIn': 'Log In',
  'auth.logInToPurchase': 'Log In to Purchase',

  // Auth - Account Create
  'auth.zipcodeOptional': 'Zipcode (optional)',
  'auth.zipcodePlaceholder': 'Zipcode',
  'auth.phoneNumberOptional': 'Phone Number (optional)',
  'auth.phoneNumber': 'Phone Number',
  'auth.birthdayOptional': 'Birthday (optional)',
  'auth.birthday': 'Birthday',
  'auth.subscribeToMarketing': 'Subscribe to email marketing',
  'auth.create': 'Create',
  'auth.creating': 'Creating...',
  'auth.alreadyHaveAccount': 'Already have an account?',

  // Auth - Reset Password
  'auth.submit': 'Submit',

  // Auth - Errors
  'auth.errorUnableToSignIn': 'Unable to sign in. Please try again.',
  'auth.errorLoginGeneric': 'An error occurred while trying to log in. Please try again.',
  'auth.errorRequiredFields': 'Required fields missing. Please complete the form.',
  'auth.errorCreateGeneric': 'An error occurred. Please try again.',
  'auth.errorCreateAccount':
    'An error occurred while trying to create an account. Please try again.',
  'auth.errorEmailNotFound': 'Email input not found. Please try again.',
  'auth.errorEnterEmail': 'Please enter your email address.',
  'auth.errorResetPassword': 'Unable to reset password. Please try again.',
  'auth.errorResetGeneric': 'An error occurred while trying to reset password. Please try again.',

  'auth.error.login_required': 'Login is required. Please enter your email and password.',
  'auth.error.invalid_credentials': 'Invalid email or password. Please try again.',
  'auth.error.account_already_exists': 'An account with this email already exists. Please log in.',
  'auth.error.invalid_email': 'The email address entered is not valid. Please try again.',
  'auth.error.weak_password': 'Your password is too weak. Please choose a stronger password.',
  'auth.error.missing_fields': 'All fields are required. Please complete the form.',
  'auth.error.invalid_name': 'The name entered is not valid. Please use only letters.',
  'auth.error.password_mismatch': 'Passwords do not match. Please try again.',
  'auth.error.missing_email': 'Please enter your email address.',
  'auth.error.email_not_found':
    'We could not find an account with this email address. Please check and try again.',
  'auth.error.email_verify': 'Please verify your email address and try again.',
  'auth.error.captcha_challenge':
    'Additional verification is required. Please complete the security challenge.',
  'auth.error.unknown_error': 'An unknown error occurred. Please try again later.',

  // Product
  'product.buyNow': 'Buy Now',
  'product.soldOut': 'Sold Out',
  'product.processing': 'Processing...',
  'product.errorAddToCart': 'Unable to add to cart.',

  // Need Access
  'access.logInToWatch': 'Log In to Watch',
  'access.purchaseAccess': 'Purchase Access',
  'access.joinOrPurchase': 'Join the Community or Purchase Access',
  'access.noAccess': 'You do not have access to this content',
  'access.joinToUnlock': 'Join to Unlock',
  'access.joinForAccess': 'Join for access',
  'access.locked': 'Locked',
  'access.joinToWatch': 'Join to Watch',
  'access.joinOrPurchaseAccess': 'Join or Purchase Access',
  'access.alreadyPurchased': 'Already Purchased?',
  'access.logInWithEmail': 'Log in with the email used for purchase',
  'access.or': '- or -',

  'access.noProductsAvailable': 'No products granting access are currently available',
  'access.upgradeToAccess': 'Upgrade to Access',

  // Perks
  'perks.posts': 'Posts',
  'perks.videos': 'Videos',
  'perks.merch': 'Merch',
  'perks.codes': 'Codes',
  'perks.music': 'Music',
  'perks.recentPosts': 'Recent Posts',
  'perks.myAccount': 'My Account',
  'perks.logout': 'Logout',
  'perks.noActiveMemberships': 'No Active Memberships.',
  'perks.generating': 'Generating...',
  'perks.noCodesFound': 'No codes found',
  'perks.noProducts': 'No Products',
  'perks.foundFor': 'found for "{query}"',
  'perks.more': 'More',
  'perks.join': 'Join',
  'perks.collectibles': 'Collectibles',
  'perks.popularPosts': 'Popular Posts',
  'perks.noPosts': 'No posts',
  'perks.adjustFilters': 'Try adjusting your filters or check back soon!',
  'perks.loadMorePosts': 'Load More Posts',
  'perks.loadMore': 'Load More',
  'perks.memberSince': 'Member Since',
  'perks.continueReading': 'Continue Reading',
  'perks.joinToContinueReading': 'Join to Continue Reading',
  'perks.beFirstToComment': 'Be the first to leave a comment!',
  'perks.addComment': 'Add a comment...',
  'perks.addCommentButton': 'Add a Comment',
  'perks.joinToComment': 'Join to comment',
  'perks.commentRemoved': '[ removed ]',
  'perks.commentHasBeenRemoved': 'Comment has been removed.',
  'perks.reply': 'Reply',
  'perks.replyToComment': 'Reply to comment...',
  'perks.viewReplies': 'View replies',
  'perks.loadMoreReplies': 'Load more replies',
  'perks.replyCount.one': '1 reply',
  'perks.replyCount.other': '{count} replies',
  'perks.filterPosts': 'Filter Posts',
  'perks.postType': 'Post Type',
  'perks.postTypeText': 'Text',
  'perks.postTypeImages': 'Images',
  'perks.postTypeVideos': 'Videos',
  'perks.postTypeProducts': 'Products',
  'perks.postTypeAudio': 'Audio',
  'a11y.play': 'Play',
  'a11y.pause': 'Pause',
  'a11y.seek': 'Seek',
  'a11y.audioArtwork': 'Audio artwork',
  'a11y.trackArtwork': '{title} artwork',
  'a11y.viewArtwork': 'View artwork',
  'perks.tags': 'Tags',
  'perks.authors': 'Authors',
  'perks.submission.submitPost': 'Submit a Post',
  'perks.submission.createPost': 'Create a Post',
  'perks.submission.postingAs': 'Posting as',
  'perks.submission.loginRequired': 'Log in to your account to share a post.',
  'perks.submission.titleLabel': 'Title',
  'perks.submission.titlePlaceholder': 'Give your post a title',
  'perks.submission.captionLabel': 'Caption',
  'perks.submission.captionPlaceholder': 'Add a caption',
  'perks.submission.addImage': 'Add a photo',
  'perks.submission.changeImage': 'Change photo',
  'perks.submission.uploading': 'Uploading...',
  'perks.submission.submit': 'Submit for review',
  'perks.submission.submitting': 'Submitting...',
  'perks.submission.post': 'Post',
  'perks.submission.posting': 'Posting...',
  'perks.submission.postAnother': 'Post another',
  'perks.submission.success': 'Submitted! Your post is waiting for review.',
  'perks.submission.successPublished': 'Your post is live.',
  'perks.submission.submitAnother': 'Submit another',
  'perks.submission.invalidImageType': 'Please choose a JPG, PNG, or GIF image.',
  'perks.submission.invalidImageSize': 'Images must be smaller than 25MB.',
  'perks.submission.errorUploadImage':
    'Something went wrong uploading your image. Please try again.',
  'perks.submission.errorSubmit': 'Something went wrong submitting your post. Please try again.',
  'perks.submission.errorLimit': 'Submission limit reached. Try again tomorrow.',
  'perks.submission.errorUsername': 'Username is unavailable for submissions',
  'perks.submission.removeImage': 'Remove photo',
  'perks.badge.artist': 'Verified',
  'perks.badge.team': 'Team',
  'perks.shadowBanned': 'Shadow banned: only this fan and moderators can see this. Lift it in your Single dashboard under Posts > Settings.',
  'perks.artistProfileManaged': 'Your artist profile is managed in the Single dashboard',
  'perks.clearAll': 'Clear all',
  'perks.applyFilters': 'Apply filters',
  'perks.backToFeed': '← Back to feed',
  'perks.backToLibrary': '← Back to library',
  'music.album': 'Album',
  'music.download': 'Download',
  'music.loginToDownload': 'Login to Download',
  'music.linkExpired': 'This link has expired.',
  'music.loginToAccess':
    'Please log in using the email provided during checkout to listen or download.',
  'perks.noVideos': 'No Videos',
  'perks.noMusic': 'No Music',
  'perks.gridView': 'Grid view',
  'perks.listView': 'List view',
  'perks.oops': 'Oops!',
  'perks.notFound': '404: Not Found',
  'perks.goBack': 'Go back',
  'perks.account': 'Account',
  'perks.displayName': 'Display Name',
  'perks.emailPreferences': 'Email Preferences',
  'perks.emailPreference.POST_NOTIFICATIONS': 'New Posts',
  'perks.emailPreference.POST_NOTIFICATIONS.description':
    'Receive an email when a new post is published.',
  'perks.emailPreference.COMMENT_NOTIFICATIONS': 'Comments and Replies',
  'perks.emailPreference.COMMENT_NOTIFICATIONS.description':
    'Receive an email when someone replies to your comment.',
  'perks.emailPreference.COMMENT_NOTIFICATIONS.description.submissions':
    'Receive an email when someone replies to your comment or comments on a post you submitted.',
  'perks.emailPreference.COMMENT_NOTIFICATIONS.description.author':
    'Receive an email when someone replies to your comment or comments on a post you published.',
  'perks.comments': 'Comments',
  'perks.view': 'View',
  'perks.transfer': 'Transfer',
  'perks.logInToViewCollectibles': 'Log In to View Collectibles',
  'perks.viewAll': 'View All',
  'perks.errorUploadAvatar': 'Unable to upload avatar. Please try again.',
  'perks.profileCardTitle': "Welcome! Let's complete your member profile.",
  'perks.walletCardTitle': 'Download Your Membership Card',
  'perks.walletPassDescription':
    'Your member pass is a digital club card for your Apple or Android device. It will be used to grant you special perks at shows and IRL.',
  'perks.walletScanDescription': 'Scan the QR code with your phone to download your pass.',
  'perks.updatingProfile': 'Updating...',
  'perks.updateProfile': 'Update Profile',

  'perks.displayNamePlaceholder': 'Type your name here...',
  'perks.usernamePlaceholder': 'Username',
  'perks.profileVisibilityNote':
    'Only your Avatar and Display Name will be visible to other members.',
  'perks.trackCount': '{count} tracks',
  'perks.trackCountOne': '{count} track',
  'perks.commentCount': '{count} comments',
  'perks.commentCountOne': '{count} comment',
  'perks.replyingTo': 'Replying to @{name}',
  'perks.postCommentButton': 'Post',
  'perks.avatarAlt': '{name} avatar',
  'perks.hiddenPostImageAlt': 'Hidden Post Image',
  'perks.postImageAlt': 'Post image',
  'perks.imageSlideAlt': 'Image Slide',
  'perks.videoThumbnailAlt': 'Video thumbnail',
  'perks.productImageAlt': 'Product',
  'perks.membershipImageAlt': 'membership image',
  'perks.tierImageAlt': 'tier image',
  'perks.authorImageAlt': 'author image',
  'perks.submission.previewAlt': 'submission preview',
  'a11y.countryCode': 'Country code',
  'a11y.dismiss': 'Dismiss',

  // Address Modal
  'address.updateAddress': 'Update Address',
  'address.firstName': 'First Name',
  'address.lastName': 'Last Name',
  'address.street': 'Street',
  'address.streetLine2': 'Street Line 2',
  'address.city': 'City',
  'address.country': 'Country',
  'address.province': 'Province',
  'address.zip': 'Zip',
  'address.phoneNumber': 'Phone Number',
  'address.update': 'Update',
  'address.updating': 'Updating...',
  'address.errorStreetRequired': 'Street address is required',
  'address.errorCountryRequired': 'Country is required',
  'address.errorZipRequired': 'Zip code is required',
  'address.errorPhoneRequired': 'Phone number is required',

  // Common
  'common.cancel': 'Cancel',
  'common.ok': 'Ok',
  'common.close': 'Close',
  'common.dialog': 'Dialog',
  'a11y.communityNav': 'Community pages',
  'a11y.likePost': 'Like post',
  'a11y.unlikePost': 'Unlike post',
  'a11y.viewComments': 'View comments',
  'a11y.previousSlide': 'Previous slide',
  'a11y.nextSlide': 'Next slide',
  'a11y.goToSlide': 'Go to slide {number}',
  'a11y.collapseComment': 'Collapse comment',
  'a11y.expandComment': 'Expand comment',
  'a11y.commentMenu': 'Comment options',
  'a11y.deleteComment': 'Delete comment',
  'a11y.likeComment': 'Like comment',
  'a11y.unlikeComment': 'Unlike comment',
  'a11y.reply': 'Reply',
  'a11y.closeReplies': 'Close replies',
  'a11y.backToFeed': 'Back to feed',
  'a11y.uploadAvatar': 'Upload avatar',
  'common.searchByTitle': 'Search by Title',
  'common.search': 'Search',
  'common.clear': 'Clear',
  'common.error': 'Error',
  'common.save': 'Save',
  'common.saving': 'Saving...',
  'common.edit': 'Edit',
  'common.delete': 'Delete',
  'common.loading': 'Loading...',
  'common.uploading': 'Uploading...',
  'common.readMore': 'read more',
  'common.readLess': 'read less',
  'common.share': 'Share',
  'common.copied': 'Copied!',
  'common.browserShareError': 'Your browser does not support sharing or clipboard features.',

  'common.alreadyMember': 'Already a member?',

  // Membership
  'membership.confirm': 'Confirm',
  'membership.nevermind': 'Nevermind',
  'membership.noGoBack': 'No, Go Back',
  'membership.yesCancel': 'Yes, Cancel',
  'membership.goBack': 'Go Back',
  'membership.continue': 'Continue',
  'membership.confirmChange': 'Confirm Change',
  'membership.termsAndConditions': 'Terms and Conditions',
  'membership.updateBillingTerms': 'Update Your Billing Terms',
  'membership.confirmYourChanges': 'Confirm Your Changes',
  'membership.confirmUpgrade': 'Confirm Upgrade',
  'membership.confirmDowngrade': 'Confirm Downgrade',
  'membership.termChange': 'Term Change',
  'membership.labelCancellation': '{label} Cancellation',
  'membership.cancelPendingTitle': 'Cancel {change}',
  'membership.yesCancelPending': 'Yes, Cancel {change}',
  'membership.revertSettings': 'Your {label} will revert back to its previous settings.',
  'membership.cancelTitle': 'Cancel {label}',
  'membership.areYouSureCancel': 'Are you sure you want to cancel?',
  'membership.cancellationEndOfTerm':
    'Your cancellation will go into effect at the end of your term.',
  'membership.stillHaveAccess':
    'However, you will still have access to your paid {label} for the remainder of your term.',
  'membership.expirationDate': '{label} Expiration Date:',
  'membership.notice': 'Notice:',
  'membership.cancellationNotice':
    "{label} cancellations won't take effect until the end of your term.",
  'membership.contactForQuestions': 'For questions about cancelling your {label}, please contact',
  'membership.contactIfUnsure':
    "If you're unsure if this applies to your specific order, please contact customer service.",
  'membership.cancelMembershipButton': 'Cancel Membership',
  'membership.currentPlan': 'Current Plan',
  'membership.newPlan': 'New Plan',
  'membership.newPlanStarts': 'Your new plan starts on',
  'membership.newTierTerm': 'New Tier Term:',
  'membership.nextBillingDateLabel': 'Next Billing Date:',
  'membership.perMonth': '/Month',
  'membership.aboutToUpgrade': 'You are about to upgrade your membership tier.',
  'membership.aboutToDowngrade': 'You are about to downgrade your membership tier.',
  'membership.viewMyPerks': 'View My Perks',
  'membership.manage': 'Manage',
  'membership.joining': 'Joining...',
  'membership.join': 'Join',
  'membership.addingToCart': 'Adding to Cart...',
  'membership.upgrade': 'Upgrade',
  'membership.downgrade': 'Downgrade',
  'membership.cancelPendingChanges': 'Cancel Pending Changes',
  'membership.active': 'Active',
  'membership.nextBillingDate': 'Next Billing Date',
  'membership.term': 'Term',
  'membership.changeTerm': '(change)',
  'membership.price': 'Price',
  'membership.free': 'FREE',
  'membership.per': 'per',
  'membership.oneTimePurchase': 'One Time Purchase',
  'membership.noMembershipsAvailable': 'No Memberships Available',
  'membership.transactionFailed': '{label} Transaction Failed - Click to Update',
  'membership.noActiveMemberships': 'No Active Memberships',
  'membership.pending': 'Pending',
  'membership.cancellation': 'Cancellation',
  'membership.upgradeDowngrade': 'Upgrade / Downgrade',
  'membership.cancelLink': 'Cancel',
  'membership.nextShipment': 'Next Shipment •',
  'membership.viewProduct': 'View Product',
  'membership.hasBeenScheduled': 'has been scheduled.',
  'membership.changingTo': 'Changing to:',
  'membership.willBeDowngradedTo': 'Your {label} will be downgraded to',
  'membership.on': 'on',
  'membership.stillHaveAccessUntil': "You'll still have access until",
  'membership.cancelChange': 'Cancel Change',
  'membership.nextBilling': 'Next Billing',
  'membership.subscriptionShipMonthly': 'Subscription orders ship monthly.',
  'membership.noUpcomingBillDates': 'No upcoming bill dates.',
  'membership.orderHistory': 'Order History',
  'membership.emailWithTracking':
    'You will receive an email with tracking once your order has shipped.',
  'membership.back': '← Back',
  'membership.shippingAddress': 'Shipping Address',
  'membership.missingAddress': 'Missing address information. Click to update.',
  'membership.editShippingAddress': 'Edit Shipping Address',
  'membership.paymentMethod': 'Payment Method',
  'membership.updatePaymentInstructions':
    'To update your payment method, use the link below. An email will be sent to you with instructions to update.',
  'membership.updatePaymentMethod': 'Update Payment Method',
  'membership.paymentFailed': 'Payment Failed',
  'membership.productImageAlt': 'product image',
  'membership.detailMembership': 'Membership',
  'membership.detailBilling': 'Billing',
  'membership.detailPlan': 'Plan',
  'membership.detailMemberId': 'Member ID',
  'membership.detailSubscriptionNumber': 'Subscription #',
  'membership.detailStatus': 'Status',
  'membership.detailTerms': 'Terms',
  'membership.detailLastPayment': 'Last Payment',
  'membership.receivedOn': 'Received {date}',
  'membership.receivedAmountOn': 'Received {amount} on {date}',
  'membership.detailName': 'Name',
  'membership.detailStreet': 'Street',
  'membership.detailCity': 'City',
  'membership.detailState': 'State',
  'membership.detailZip': 'ZIP',
  'membership.detailCountry': 'Country',
  'membership.detailPhone': 'Phone',
  'membership.detailCard': 'Card',
  'membership.detailNumber': 'Number',
  'membership.detailExpires': 'Expires',
  'membership.detailMethod': 'Method',
  'membership.manageLabel': 'Manage {label}',

  // Music
  'music.exclusive': 'Exclusive',
  'music.trackTitle': 'Title',
  'music.songCount': '{count} songs',
  'music.songCountOne': '{count} song',
  'music.allTracksAvailableIn': 'ALL TRACKS AVAILABLE IN:',
  'music.preorderUnlock': 'Pre-order now and unlock access to stream or download on {date}.',
  'music.downloadUnavailable': 'Download Unavailable',
  'music.downloadLimitReached': "You've reached the download limit. Please contact the shop.",
  'music.downloadFailed': 'Download failed. Please try again.',
  'music.downloadNotAllowed': 'Sign in with the email you used to purchase.',
  'music.downloadCodeUnavailable': "This download isn't available. Please contact the shop.",
  'music.downloadStarted': 'Download started',
  'music.downloadStartedHint': 'Check your Downloads or Files app.',
  'music.openInBrowserTitle': 'Download blocked',
  'music.openInBrowserBody':
    'The in-app browser you are using is blocking downloads. You will need to open this page in an external browser.',
  'music.openInBrowserHint': 'Tap the ••• icon and select "Open in external browser" to continue.',
  'music.demoArtist': 'Test Artist',
  'music.demoAlbum': 'Demo Album',
  'music.demoTrack': 'Track {n}',
  'a11y.previousTrack': 'Previous track',
  'a11y.nextTrack': 'Next track',
  'a11y.playAll': 'Play all',

  // Video
  'video.startsIn': 'Starts in:',
  'video.previewTitle': 'Video Title',
  'video.previewArtist': 'Artist Name',
  'video.previewDescription': 'Video description',
};
